/**
 * Billable-items rollup helpers — keep cancelled items out of summary totals/counts.
 *
 * v0.14e: Cancelled items are visible in the BillableItemsTable, but per the
 * cancel-line-item feature contract they MUST be excluded from any rollup
 * displayed elsewhere on the page (section header summary, Financial Summary
 * widget, etc).
 *
 * `invoiced` / `paid` aggregates need no filter at the JS layer because by the
 * v0.14 cancel-guard a cancelled billable item can have no live invoice_items
 * — its contribution is already 0.
 */
export interface CancellableBillableItem {
  amount?: number | null;
  cancelled?: boolean | null;
}

/**
 * Compute the section-header summary `{ count, total }` for billable items.
 *
 * Cancelled items are dropped before counting and summing.
 */
export function summarizeBillableItemsForSection<T extends CancellableBillableItem>(
  items: T[]
): { count: number; total: number; activeItems: T[] } {
  const activeItems = items.filter((item) => !item.cancelled);
  const total = activeItems.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  return {
    count: activeItems.length,
    total,
    activeItems,
  };
}
